import { Component, ReactNode, useEffect, useState, lazy, Suspense } from "react";
import { consumeError } from "./lib/errorTracking";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConsentBanner } from "./components/ConsentBanner";
import { UpdateNotification } from "./components/PWA/UpdateNotification";
import { DomainChangeBanner } from "./components/DomainChangeBanner";
import { consentManager } from "./lib/consent";
import { initAnalytics, unloadAnalytics } from "./lib/analytics";
import { initErrorTracking, updateErrorTrackingConsent } from "./lib/errorTracking";
import { initMaze, isTestMode } from "./lib/maze";
import { MAZE_CONFIG } from "./lib/config";
import { isDevelopment } from "./lib/devHelper";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AvatarProvider } from "./contexts/AvatarContext";
import { BudgetProvider } from "./contexts/BudgetContext";
import { GamificationProvider } from "./contexts/GamificationContext";
import { PWAProvider } from "./contexts/PWAContext";
import { ProtectedRoute } from "./components/Layout/ProtectedRoute";
import { RouteLoading } from "./components/Loading/RouteLoading";

// Lazy-loaded route components for code splitting
const Home = lazy(() => import("./pages/Home"));
const BudgetDetail = lazy(() => import("./pages/BudgetDetail"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Settings = lazy(() => import("./pages/Settings"));
const Badges = lazy(() => import("./pages/Badges"));
const Profile = lazy(() => import("./pages/Profile"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const PrivacyAnalytics = lazy(() => import("./pages/PrivacyAnalytics"));
const Terms = lazy(() => import("./pages/Terms"));
const Imprint = lazy(() => import("./pages/Imprint"));
const DataExport = lazy(() => import("./pages/DataExport"));
const Insights = lazy(() => import("./pages/Insights"));
const LanguageDebug = lazy(() => import("./pages/LanguageDebug"));
const TestRunner = lazy(() => import("./pages/TestRunner"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Install = lazy(() => import("./pages/Install"));
const Wrapped = lazy(() => import("./pages/Wrapped"));

// Preload critical routes on idle to make navigation feel instant
const preloadCriticalRoutes = () => {
  const preload = () => {
    import("./pages/Home");
    import("./pages/Expenses");
    import("./pages/Settings");
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(preload);
  } else {
    setTimeout(preload, 2000);
  }
};

// Error boundary that integrates with Highlight
class HighlightErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    consumeError(error, "React Error Boundary", errorInfo);
  }

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Try to get user's stored language for minimal i18n
      const fallbackStrings: Record<string, Record<string, string>> = {
        en: { heading: 'Oops!', desc: "We hit a snag. Don't worry, your data is safe.", refresh: 'Refresh Page', home: 'Back to Home' },
        uk: { heading: 'Ой!', desc: 'Щось пішло не так. Ваші дані в безпеці.', refresh: 'Оновити сторінку', home: 'На головну' },
        de: { heading: 'Hoppla!', desc: 'Etwas ist schiefgelaufen. Keine Sorge, Ihre Daten sind sicher.', refresh: 'Seite aktualisieren', home: 'Zur Startseite' },
        fr: { heading: 'Oups !', desc: 'Un problème est survenu. Vos données sont en sécurité.', refresh: 'Actualiser la page', home: "Retour à l'accueil" },
        es: { heading: '¡Ups!', desc: 'Algo salió mal. No te preocupes, tus datos están seguros.', refresh: 'Actualizar página', home: 'Volver al inicio' },
        nl: { heading: 'Oeps!', desc: 'Er ging iets mis. Maak je geen zorgen, je gegevens zijn veilig.', refresh: 'Pagina vernieuwen', home: 'Naar startpagina' },
        pl: { heading: 'Ups!', desc: 'Coś poszło nie tak. Nie martw się, Twoje dane są bezpieczne.', refresh: 'Odśwież stronę', home: 'Strona główna' },
        it: { heading: 'Ops!', desc: 'Qualcosa è andato storto. I tuoi dati sono al sicuro.', refresh: 'Aggiorna pagina', home: 'Torna alla home' },
        ja: { heading: 'おっと！', desc: '問題が発生しました。データは安全です。', refresh: 'ページを更新', home: 'ホームに戻る' },
        tr: { heading: 'Hay aksi!', desc: 'Bir sorun oluştu. Verileriniz güvende.', refresh: 'Sayfayı yenile', home: 'Ana sayfaya dön' },
        pt: { heading: 'Ops!', desc: 'Algo deu errado. Seus dados estão seguros.', refresh: 'Atualizar página', home: 'Voltar ao início' },
      };
      let lang = 'en';
      try { lang = localStorage.getItem('cashee-language')?.split('-')[0] || 'en'; } catch {}
      const s = fallbackStrings[lang] || fallbackStrings.en;

      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="text-5xl">🌿</div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">{s.heading}</h1>
              <p className="text-muted-foreground">{s.desc}</p>
            </div>
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => window.location.reload()} 
                className="w-full"
                size="lg"
              >
                {s.refresh}
              </Button>
              <Button 
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full"
                size="lg"
              >
                {s.home}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inner App component that uses auth context
const AppContent = () => {
  const { user, isLoading } = useAuth();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    const consent = consentManager.getConsent();
    const shouldShowBanner = !consent.bannerDismissed;
    setShowBanner(shouldShowBanner);
    // Preload critical routes once auth state is resolved
    preloadCriticalRoutes();
  }, [isLoading, user]);

  useEffect(() => {
    if (isLoading) return;
    const consent = consentManager.getConsent();
    
    initErrorTracking();
    
    if (consent.analytics) {
      initAnalytics();
    }

    if (MAZE_CONFIG.testModeOnly && isTestMode()) {
      initMaze();
    } else if (!MAZE_CONFIG.testModeOnly && MAZE_CONFIG.enabled) {
      initMaze();
    }

    const handleConsentChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newConsent = customEvent.detail;
      
      if (newConsent.analytics) {
        initAnalytics();
      } else {
        unloadAnalytics();
      }
      
      updateErrorTrackingConsent(newConsent.crash);
      setShowBanner(!newConsent.bannerDismissed);
    };

    window.addEventListener('consent:changed', handleConsentChange);
    return () => window.removeEventListener('consent:changed', handleConsentChange);
  }, [isLoading]);

  const RouterShell = () => {
    const location = useLocation();
    const onboardingComplete = localStorage.getItem('onboardingComplete') === 'true';
    const isAuthRoute = location.pathname.startsWith('/auth');
    const isOnboardingRoute = location.pathname.startsWith('/onboarding');
    const enableUpdateNotification = !!user && onboardingComplete && !isAuthRoute && !isOnboardingRoute;

    return (
      <>
        {enableUpdateNotification && <UpdateNotification />}
        <DomainChangeBanner />
        {!isLoading && showBanner && !isAuthRoute && !isOnboardingRoute && <ConsentBanner />}
        <BudgetProvider>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              {/* Protected routes */}
              <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/budget" element={<ProtectedRoute><BudgetDetail /></ProtectedRoute>} />
              <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/settings/privacy-analytics" element={<ProtectedRoute><PrivacyAnalytics /></ProtectedRoute>} />
              <Route path="/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/data-export" element={<ProtectedRoute><DataExport /></ProtectedRoute>} />
              <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
              <Route path="/wrapped" element={<ProtectedRoute><Wrapped /></ProtectedRoute>} />
              
              {/* Auth routes (public) */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/onboarding" element={<Onboarding />} />
              
              {/* Public routes */}
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/imprint" element={<Imprint />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/install" element={<Install />} />
              <Route path="/debug/language" element={isDevelopment ? <LanguageDebug /> : <NotFound />} />
              <Route path="/debug/tests" element={isDevelopment ? <TestRunner /> : <NotFound />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BudgetProvider>
      </>
    );
  };

  return (
    <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouterShell />
      </BrowserRouter>
    </>
  );
};

// Main App wrapper with providers
const App = () => {
  return (
    <HighlightErrorBoundary>
      <AuthProvider>
        <AvatarProvider>
          <PWAProvider>
            <GamificationProvider>
              <TooltipProvider>
                <AppContent />
              </TooltipProvider>
            </GamificationProvider>
          </PWAProvider>
        </AvatarProvider>
      </AuthProvider>
    </HighlightErrorBoundary>
  );
};

export default App;
